import { PROPERTY_GRID_SUBJECT_PROPERTY } from '@aloft/toolkit-shared';
import { Block, Input, Typography } from '@aloft/toolkit-sui';

import { useEffect, useState } from 'react';
import { FieldPath, useWatch } from 'react-hook-form';

import { readOnlyFieldTypographyProps } from '../../../components/PropertyGrid/fields/styles.ts';
import {
  PropertyGridForm,
  PropertyGridSectionProps,
} from '../../../components/PropertyGrid/types.ts';
import { handleUpdatePropertyData } from '../../../components/PropertyGrid/utils.ts';
import { selectHasArvOption } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectAivArvActiveTab } from '../../../redux/features/selectors/uiSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';

const DataSourceField = ({
  columnIndex,
  isArv = false,
  control,
  getValues,
  setValue,
}: {
  columnIndex: PropertyGridSectionProps['columnIndex'];
  isArv?: boolean;
} & Pick<PropertyGridSectionProps, 'control' | 'getValues' | 'setValue'>) => {
  const dispatch = useAppDispatch();

  const arvFieldPrefix = isArv ? 'arv.' : '';
  const fieldName: FieldPath<PropertyGridForm> = `columns.${columnIndex}.propertyData.editableData.${arvFieldPrefix}improvements.featureGridDataSources`;
  const value = useWatch({ control, name: fieldName });

  const [displayValue, setDisplayValue] = useState<string | undefined>(value);
  const propertyId = getValues(`columns.${columnIndex}.propertyId`);

  useEffect(() => {
    setDisplayValue(value);
  }, [value, setDisplayValue]);

  const handleSetFieldValue = (newValue?: string) => {
    setValue(fieldName, newValue);
    // get updated propertyData
    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
  };

  return (
    <Input
      type="text"
      __dangerDoNotUseSx={{
        width: '100%',
        height: '32px',
        padding: '4px 8px',
        border: 'none',
        borderRadius: '0',
        fontSize: '14px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textWrap: 'nowrap',
        whiteSpace: 'nowrap',
      }}
      value={displayValue}
      onChange={setDisplayValue}
      onClear={() => setDisplayValue(undefined)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setDisplayValue(value);
          if (e.currentTarget instanceof HTMLInputElement) e.currentTarget.blur();
        } else if (e.key === 'Enter' || e.key === 'NumpadEnter') {
          handleSetFieldValue(displayValue);
          if (e.currentTarget instanceof HTMLInputElement) e.currentTarget.blur();
        }
      }}
      onBlur={() => {
        handleSetFieldValue(displayValue);
      }}
    />
  );
};

export const FeatureGridDataSourcesSection = ({
  columnIndex,
  fieldTypes,
  control,
  getValues,
  setValue,
}: PropertyGridSectionProps) => {
  const hasArvOption = useAppSelector(selectHasArvOption);
  const activeTab = useAppSelector(selectAivArvActiveTab);

  const columnType = getValues(`columns.${columnIndex}.type`);
  const propertyId = getValues(`columns.${columnIndex}.propertyId`);
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  if (columnType !== 'property') {
    return (
      <Block>
        <Typography {...readOnlyFieldTypographyProps} />
      </Block>
    );
  }

  const showAivComps = !hasArvOption || activeTab === 'all' || activeTab === 'aiv';
  const showArvComps = !hasArvOption || activeTab === 'all' || activeTab === 'arv';

  const showLabels = fieldTypes.includes('label');

  return (
    <>
      {showLabels && <Typography {...readOnlyFieldTypographyProps}>Data Source(s)</Typography>}
      {(!isSubject || showAivComps) && (
        <DataSourceField
          columnIndex={columnIndex}
          control={control}
          getValues={getValues}
          setValue={setValue}
        />
      )}
      {isSubject && hasArvOption && showArvComps && (
        <DataSourceField
          columnIndex={columnIndex}
          isArv
          control={control}
          getValues={getValues}
          setValue={setValue}
        />
      )}
    </>
  );
};
